import { useEffect, useMemo, useRef, useState } from "react";
import { fetchAdminComplexes } from "../../../admin/api";
import EditComplexModal from "../EditComplexModal";
import { adminStyles as styles } from "../shared/adminStyles";
import { normalizeSearchValue } from "../utils/adminFormatters";
import { buildSipRows, compareTransferRows } from "../utils/sipHelpers";
import WorkspaceSelectionToggle from "../workspaceSelection/WorkspaceSelectionToggle";
import EditComplexButton from "../EditComplexButton";
import { createSipClientSelectionItem, createSipClientSelectionItemFromTransfer, createTransferSelectionItem, getWorkspaceSelectionKey } from "../workspaceSelection/workspaceSelectionUtils";
import TemporarySelectionBar from "../workspaceSelection/TemporarySelectionBar";
import TemporarySelectionCheckbox from "../workspaceSelection/TemporarySelectionCheckbox";
import useTemporarySelection from "../workspaceSelection/useTemporarySelection";
import BulkSipStatusActions from "../bulkSipActions/BulkSipStatusActions";
import { applyBulkStatusResultsToComplexes } from "../bulkSipActions/bulkSipStatusUtils";
import BulkSipTableEditorAction from "../bulkSipActions/BulkSipTableEditorAction";
import { applyBulkEditResultsToComplexes } from "../bulkSipActions/bulkSipTableEditorUtils";
import { useSessionSipActivity } from "../sessionActivity/SessionSipActivityContext";
import SessionChangedBadge from "../sessionActivity/SessionChangedBadge";
import SessionActivityControls from "../sessionActivity/SessionActivityControls";
import SavedViewsControls from "../savedViews/SavedViewsControls";
import { useAdminDataRefresh } from "../dataRefresh/AdminDataRefreshContext";
import AdminStatusBadge from "../shared/AdminStatusBadge";
import { useAdminTextSearch } from "../utils/useAdminTextSearch";
import SipEndpointsDirectory from "./SipEndpointsDirectory";
import { useWorkspaceSelection } from "../workspaceSelection/WorkspaceSelectionContext";
import { useWorkspaceSelectionMode } from "../workspaceSelection/WorkspaceSelectionModeContext";
import WorkspaceSelectionModeBar from "../workspaceSelection/WorkspaceSelectionModeBar";
const DEFAULT_TRANSFERS_VIEW = {
  search: "",
  filters: { onlySessionChanged: false },
  sort: { field: "transfer_number", direction: "asc" },
  pageSize: null,
  settings: { sipView: "current" },
};
export default function TransfersSection() {
  const [complexes, setComplexes] = useState([]);
  const [editingComplex, setEditingComplex] = useState(null);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [onlySessionChanged, setOnlySessionChanged] = useState(false);
  const [sipView, setSipView] = useState("current");
  const documentsScrollRef = useRef(null);
  const restoredWorkspaceViewRef = useRef(false);
  const { isChanged } = useSessionSipActivity();
  const { invalidate, versions } = useAdminDataRefresh();
  const debouncedQuery = useAdminTextSearch(query);
  const { items: workspaceItems } = useWorkspaceSelection();
  const { activeGroup, activeView, restoreState, exitMode, registerSectionState } = useWorkspaceSelectionMode();
  const isWorkspaceMode = activeGroup === "transfers";
  const workspaceTransferItems = useMemo(() => workspaceItems.filter((item) => item.type === "transfer"), [workspaceItems]);
  const workspaceTransferKeys = useMemo(() => new Set(workspaceTransferItems.map(getWorkspaceSelectionKey)), [workspaceTransferItems]);

  useEffect(() => {
    let active = true;

    async function loadComplexes() {
      try {
        setIsLoading(true);
        setError("");

        const data = await fetchAdminComplexes();

        if (!active) return;

        const items = Array.isArray(data) ? data : data?.items || [];
        setComplexes(items);
      } catch (loadError) {
        if (!active) return;

        setError(
          loadError instanceof Error
            ? loadError.message
            : "Не удалось загрузить СИПы"
        );
      } finally {
        if (active) setIsLoading(false);
      }
    }

    loadComplexes();

    return () => {
      active = false;
    };
  }, [versions.transfers]);

  const transferRows = useMemo(
    () => buildSipRows(complexes).sort(compareTransferRows),
    [complexes]
  );
  const filteredRows = useMemo(() => {
    const normalizedQuery = normalizeSearchValue(debouncedQuery.value);

    return transferRows.filter((row) => {
      if (isWorkspaceMode && !workspaceTransferKeys.has(getWorkspaceSelectionKey(createTransferSelectionItem(row)))) return false;
      const selectionItem = createSipClientSelectionItem(row);
      if (onlySessionChanged && !isChanged(selectionItem)) return false;
      if (!normalizedQuery) {
        return true;
      }

      const haystack = [row.complexName, row.transfer_number, row.sip_number, row.clientName]
        .map((value) => normalizeSearchValue(value))
        .join(" ");

      return haystack.includes(normalizedQuery);
    });
  }, [debouncedQuery.value, isChanged, isWorkspaceMode, onlySessionChanged, transferRows, workspaceTransferKeys]);
  const resolvedWorkspaceRows = useMemo(
    () => transferRows.filter((row) => workspaceTransferKeys.has(getWorkspaceSelectionKey(createTransferSelectionItem(row)))),
    [transferRows, workspaceTransferKeys]
  );

  const visibleSelectionItems = useMemo(
    () => filteredRows.map(createTransferSelectionItem),
    [filteredRows]
  );
  const temporarySelection = useTemporarySelection(visibleSelectionItems);
  const { replaceSelection: replaceTemporarySelection } = temporarySelection;
  const selectedSipItems = useMemo(
    () => temporarySelection.selectedItems.map(createSipClientSelectionItemFromTransfer),
    [temporarySelection.selectedItems]
  );
  const currentSavedView = useMemo(() => ({
    search: query,
    filters: { onlySessionChanged },
    sort: { field: "transfer_number", direction: "asc" },
    pageSize: null,
    settings: { sipView },
  }), [onlySessionChanged, query, sipView]);
  const applySavedView = (view) => {
    setQuery(view.search || "");
    setOnlySessionChanged(Boolean(view.filters?.onlySessionChanged));
    setSipView(view.settings?.sipView === "directory" ? "directory" : "current");
  };
  useEffect(() => {
    if (isWorkspaceMode && activeView && !restoredWorkspaceViewRef.current) {
      restoredWorkspaceViewRef.current = true;
      applySavedView(activeView);
      setSipView("current");
    }
  }, [activeView, isWorkspaceMode]);
  useEffect(() => {
    registerSectionState("transfers", currentSavedView);
  }, [currentSavedView, registerSectionState]);
  useEffect(() => {
    if (isWorkspaceMode) replaceTemporarySelection(visibleSelectionItems);
  }, [isWorkspaceMode, replaceTemporarySelection, visibleSelectionItems]);
  const exitWorkspaceMode = () => {
    if (restoreState) applySavedView(restoreState);
    exitMode();
  };

  const handleSaved = (savedComplex, options = {}) => {
    if (!savedComplex?._id) {
      if (options.close !== false) setEditingComplex(null);
      return;
    }
    if (options.close === false) setEditingComplex(savedComplex);
    else setEditingComplex(null);
    invalidate(["transfers"]);
  };

  const handleStatusApplied = (results) => {
    setComplexes((current) => applyBulkStatusResultsToComplexes(current, results));
  };

  const handleBulkEditApplied = (results) => {
    setComplexes((current) => applyBulkEditResultsToComplexes(current, results));
  };

  const scrollSipTable = (position) => {
    const scrollContainer = documentsScrollRef.current;
    if (!scrollContainer) return;
    scrollContainer.scrollTo({ top: position === "top" ? 0 : scrollContainer.scrollHeight });
  };

  return (
    <>
      <section style={styles.card}>
        <div className="workspace-selection-search-header">
          <div className="workspace-selection-search-header__saved">
            <SavedViewsControls
              sectionId="transfers"
              currentView={currentSavedView}
              defaultView={DEFAULT_TRANSFERS_VIEW}
              onApply={applySavedView}
            />
          </div>
          {isWorkspaceMode && (
            <WorkspaceSelectionModeBar
              count={filteredRows.length}
              missingCount={Math.max(0, workspaceTransferItems.length - resolvedWorkspaceRows.length)}
              onExit={exitWorkspaceMode}
            />
          )}
        </div>
        <div style={{ ...styles.filterActionsBar, justifyContent: "flex-end" }}>
          <p style={styles.resultCount}>
            Найдено: {filteredRows.length}, всего: {transferRows.length}
          </p>
        </div>

        <div style={styles.controls}>
          <input
            placeholder="Поиск по ЖК, номеру перевода, СИПу или клиенту..."
            style={{ ...styles.input, gridColumn: "1 / -1" }}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <SessionActivityControls
          onlyChanged={onlySessionChanged}
          onOnlyChangedChange={setOnlySessionChanged}
        />
      </section>

      <div style={{ ...styles.viewControls, margin: "var(--admin-gap) 0" }} aria-label="Вид SIP">
        <button type="button" style={{ ...styles.viewButton, ...(sipView === "current" ? styles.viewButtonActive : {}) }} onClick={() => setSipView("current")}>Текущие SIP</button>
        <button type="button" style={{ ...styles.viewButton, ...(sipView === "directory" ? styles.viewButtonActive : {}) }} onClick={() => setSipView("directory")}>Справочник SIP</button>
      </div>

      <section style={{ ...styles.documentsCard, display: sipView === "current" ? "flex" : "none" }}>
        <div style={styles.documentsHeader}>
          <h2 style={styles.documentsTitle}>Таблица СИПов</h2>
          <div className="sip-scroll-controls">
            <span style={styles.muted}>{filteredRows.length} / {transferRows.length}</span>
            <button
              type="button"
              className="admin-action-icon-button"
              aria-label="Перейти в начало списка"
              onClick={() => scrollSipTable("top")}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 15 6-6 6 6" /></svg>
            </button>
            <button
              type="button"
              className="admin-action-icon-button"
              aria-label="Перейти в конец списка"
              onClick={() => scrollSipTable("bottom")}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
            </button>
          </div>
        </div>

        <TemporarySelectionBar
          selectedItems={temporarySelection.selectedItems}
          onClear={temporarySelection.clearSelection}
          additionalActions={
            <>
              <BulkSipTableEditorAction
                items={temporarySelection.selectedItems}
                entityType="transfer"
                label="Редактировать таблицу СИПов"
                onApplied={handleBulkEditApplied}
                sourceSection="transfers"
                buttonStyle={styles.viewButton}
              />
              <BulkSipStatusActions
                items={selectedSipItems}
                onApplied={handleStatusApplied}
                sourceSection="transfers"
                buttonStyle={styles.viewButton}
              />
            </>
          }
        />

        <div ref={documentsScrollRef} style={styles.documentsScroll}>
          {isLoading ? (
            <div style={styles.state}>Загрузка СИПов...</div>
          ) : error ? (
            <div style={styles.state}>{error}</div>
          ) : transferRows.length === 0 ? (
            <div style={styles.state}>Нет СИПов для отображения</div>
          ) : filteredRows.length === 0 ? (
            <div style={styles.state}>Ничего не найдено</div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <colgroup>
                  <col style={{ width: "42px" }} />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col style={{ width: "76px" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={styles.th}>
                      <TemporarySelectionCheckbox
                        checked={temporarySelection.allVisibleSelected}
                        indeterminate={temporarySelection.partiallyVisibleSelected}
                        onChange={temporarySelection.toggleVisible}
                        label="Выбрать все видимые СИПы"
                      />
                    </th>
                    <th style={styles.th}>ЖК</th>
                    <th style={styles.th}>Номер перевода</th>
                    <th style={styles.th}>СИП</th>
                    <th style={styles.th}>Клиент</th>
                    <th style={styles.th}>Статус</th>
                    <th style={styles.th}>Действия</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRows.map((row, index) => {
                    const rowBackground =
                      index % 2 === 0 ? "var(--card-bg)" : "var(--card-bg-soft)";

                    return (
                      <tr
                        key={`${row.complexId}-${row.sipClientId}`}
                        style={{ background: rowBackground }}
                        onMouseEnter={(event) => {
                          event.currentTarget.style.background = "var(--accent-soft)";
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = rowBackground;
                        }}
                      >
                        <td style={styles.td}>
                          <TemporarySelectionCheckbox
                            checked={temporarySelection.isTemporarilySelected(
                              visibleSelectionItems[index]
                            )}
                            onChange={() => temporarySelection.toggleOne(visibleSelectionItems[index])}
                            label={`Выбрать СИП клиента ${row.clientName || "Без имени"}`}
                          />
                        </td>
                        <td style={styles.td}>
                          <div style={styles.name}>{row.complexName}</div>
                        </td>
                        <td style={styles.td}>{row.transfer_number || "—"}</td>
                        <td style={styles.td}>{row.sip_number || "—"}</td>
                        <td style={styles.td}>
                          {row.clientName || "—"}<br />
                          <SessionChangedBadge item={createSipClientSelectionItem(row)} />
                        </td>
                        <td style={styles.td}><AdminStatusBadge active={row.clientStatus} /></td>
                        <td style={{ ...styles.td, ...styles.actionsTd }}>
                          <div className="admin-row-actions">
                            <WorkspaceSelectionToggle item={createTransferSelectionItem(row)} />
                            <EditComplexButton
                              label="Открыть ЖК"
                              onClick={() => setEditingComplex(row.complex)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <SipEndpointsDirectory active={sipView === "directory"} />

      <EditComplexModal
        complex={editingComplex}
        isOpen={Boolean(editingComplex)}
        onClose={() => setEditingComplex(null)}
        onSaved={handleSaved}
      />
    </>
  );
}
